/**
 * G2eGente - QR Code para Diagnóstico NR01 Público
 * Gera QR Code para compartilhamento do link de resposta
 */
import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  ArrowLeft,
  Download,
  Copy,
  Check,
  QrCode,
  Share2,
  Printer
} from 'lucide-react';
import { toast } from 'sonner';

export default function NR01QRCode() {
  const navigate = useNavigate();
  const { diagnosticId, codigo } = useParams();
  const qrRef = useRef(null);
  const [copied, setCopied] = useState(false);
  
  const baseUrl = window.location.origin;
  const publicLink = `${baseUrl}/p/nr01/${codigo}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(publicLink);
    setCopied(true);
    toast.success('Link copiado!');
    setTimeout(() => setCopied(false), 3000);
  };

  const handleDownloadQR = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = 400;
      canvas.height = 400;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, 400, 400);
      
      const pngFile = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.download = `QRCode_NR01_${codigo}.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    toast.success('QR Code baixado!');
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const svg = qrRef.current?.querySelector('svg');
    if (!svg || !printWindow) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Code - Diagnóstico NR01</title>
          <style>
            body {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              font-family: system-ui, -apple-system, sans-serif;
            }
            .container {
              text-align: center;
              padding: 40px;
            }
            h1 {
              color: #1e293b;
              font-size: 24px;
              margin-bottom: 8px;
            }
            p {
              color: #64748b;
              font-size: 14px;
              margin-bottom: 24px;
            }
            .qr-wrapper {
              background: white;
              padding: 20px;
              border: 2px solid #e2e8f0;
              border-radius: 16px;
              display: inline-block;
              margin-bottom: 24px;
            }
            .link {
              font-family: monospace;
              font-size: 12px;
              color: #64748b;
              word-break: break-all;
              max-width: 300px;
            }
            .footer {
              margin-top: 32px;
              font-size: 12px;
              color: #94a3b8;
            }
          </style>
        </head>
        <body>
          <div class="container">
            <h1>Diagnóstico de Riscos Psicossociais</h1>
            <p>Escaneie o QR Code para responder ao questionário</p>
            <div class="qr-wrapper">
              ${svgData}
            </div>
            <p class="link">${publicLink}</p>
            <p class="footer">Suas respostas são 100% anônimas</p>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <QrCode className="w-7 h-7 text-amber-600" />
              QR Code do Diagnóstico
            </h1>
            <p className="text-slate-500">Compartilhe para coletar respostas</p>
          </div>
        </div>

        {/* QR Code Card */}
        <Card className="mb-6">
          <CardContent className="p-8">
            <div className="flex flex-col items-center">
              <div 
                ref={qrRef}
                className="bg-white p-6 rounded-2xl border-2 border-slate-200 shadow-sm mb-6"
              >
                <QRCodeSVG 
                  value={publicLink}
                  size={280}
                  level="H"
                  includeMargin={true}
                  bgColor="#ffffff"
                  fgColor="#1e293b"
                />
              </div>
              
              <p className="text-center text-slate-600 mb-2">
                Escaneie para acessar o diagnóstico
              </p>
              <p className="text-center text-sm text-slate-400 mb-6">
                Respostas 100% anônimas
              </p>

              {/* Link */}
              <div className="w-full flex gap-2 mb-6">
                <Input 
                  value={publicLink}
                  readOnly
                  className="font-mono text-sm bg-slate-50"
                />
                <Button
                  variant="outline"
                  onClick={handleCopyLink}
                  className={copied ? 'bg-emerald-50 border-emerald-300' : ''}
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 justify-center">
                <Button onClick={handleDownloadQR} className="bg-amber-600 hover:bg-amber-700">
                  <Download className="w-4 h-4 mr-2" />
                  Baixar PNG
                </Button>
                <Button variant="outline" onClick={handlePrint}>
                  <Printer className="w-4 h-4 mr-2" />
                  Imprimir
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({
                        title: 'Diagnóstico NR01',
                        text: 'Responda ao diagnóstico de riscos psicossociais',
                        url: publicLink
                      });
                    } else {
                      handleCopyLink();
                    }
                  }}
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Compartilhar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Como usar</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-slate-600">
            <div className="flex gap-3">
              <div className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0 font-medium">
                1
              </div>
              <p>Imprima o QR Code ou exiba em uma tela em local visível</p>
            </div>
            <div className="flex gap-3">
              <div className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0 font-medium">
                2
              </div>
              <p>Os participantes escaneiam com a câmera do celular</p>
            </div>
            <div className="flex gap-3">
              <div className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0 font-medium">
                3
              </div>
              <p>Respondem diretamente no navegador, sem precisar baixar app</p>
            </div>
            <div className="flex gap-3">
              <div className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0 font-medium">
                4
              </div>
              <p>As respostas são coletadas de forma 100% anônima</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
